import { ATIVO_TIPO, FUNDO_DISTRIBUICAO, PUBLICO_ALVO } from '@investirapri/financa.js/lib/enums';
import { formats } from 'investira.sdk';
import INDICADORES from '../../../enums/INDICADORES';
import FUNDO_SITUACAO from '../../../enums/FUNDO_SITUACAO';

/** Confirma a presença de um filtro ou dado, preservando falso e zero. */
function pvHasValue(pValue) {
  if (Array.isArray(pValue)) {
    return pValue.length > 0;
  }

  return pValue !== '' && pValue !== null && typeof pValue !== 'undefined';
}

/** Identifica quando Situação contém somente o valor implícito da pesquisa. */
function pvHasOnlyImplicitSituacao(pValue) {
  const xValues = Array.isArray(pValue) ? pValue : String(pValue || '').split(',');

  return xValues.length === 1 && xValues[0] === FUNDO_SITUACAO.EM_FUNCIONAMENTO_NORMAL.value;
}

/** Extrai os atributos presentes na expressão de ordenação da pesquisa. */
function pvGetSortFields(pSort) {
  return String(pSort || '')
    .split(',')
    .map(pOrder => pOrder.trim().split(/\s+/)[0])
    .filter(Boolean);
}

/** Lê primeiro o payload original e usa o modelo normalizado apenas como fallback. */
function pvReadValue(pItem, pRawName, pNormalizedName = pRawName) {
  const xRaw = pItem?.raw || {};

  if (Object.prototype.hasOwnProperty.call(xRaw, pRawName)) {
    return xRaw[pRawName];
  }

  return pItem?.[pNormalizedName];
}

/** Formata um número no padrão visual do app. */
function pvFormatNumber(pValue, pDecimals = 2, pCurrency = false) {
  return formats.formatNumber(pValue, pDecimals, true, pCurrency);
}

/** Formata um valor percentual. */
function pvFormatPercent(pValue) {
  return `${pvFormatNumber(pValue)}%`;
}

/** Formata explicitamente valores booleanos sem usar truthiness de strings. */
function pvFormatBoolean(pValue, pLabels = ['Sim', 'Não']) {
  if (pValue === true || pValue === 'true' || pValue === 1 || pValue === '1') {
    return pLabels[0];
  }

  if (pValue === false || pValue === 'false' || pValue === 0 || pValue === '0') {
    return pLabels[1];
  }

  return pValue;
}

/** Localiza a descrição de um ou mais valores de domínio. */
function pvFormatOptions(pOptions, pValue, pValueKey, pDescriptionKey = 'descricao') {
  const xValues = Array.isArray(pValue) ? pValue : String(pValue).split(',');

  return xValues
    .map(pCurrentValue => {
      const xOption = Object.values(pOptions).find(
        pOption => String(pOption[pValueKey]) === String(pCurrentValue)
      );

      return xOption?.[pDescriptionKey] || pCurrentValue;
    })
    .join(', ');
}

const wResultChipFields = [
  ['ativo_tipo_id', 'Tipo de ativo', 'ativoTipo', 'ativoTipoId'],
  ['benchmark_indicador_id', 'Benchmark', 'benchmark', 'benchmarkIndicadorId'],
  ['situacao', 'Situação', 'situacao'],
  ['exclusivo', 'Exclusivo', 'boolean'],
  ['fechado', 'Fundo aberto/fechado', 'openClosed'],
  ['longo', 'Longo prazo', 'boolean'],
  ['cotas', 'Fundo de cotas', 'boolean'],
  ['taxa_adm', 'Taxa de administração', 'percent'],
  ['condominio_fechado', 'Condomínio', 'openClosed'],
  ['responsabilidade_limitada', 'Responsabilidade limitada', 'boolean'],
  ['qualificado', 'Qualificado', 'boolean'],
  ['profissional', 'Profissional', 'boolean'],
  ['publico_alvo', 'Público-alvo', 'publicoAlvo'],
  ['distribuicao', 'Distribuição', 'distribuicao'],
  ['aplicacao_min', 'Aplicação mínima', 'currency'],
  ['resgate_carencia', 'Prazo para Resgate', 'days'],
  ['previdenciario', 'Previdenciário', 'boolean'],
  ['estruturado', 'Estruturado', 'boolean'],
  ['esg', 'ESG', 'boolean'],
  ['retorno', 'Retorno', 'integer', null, 3],
  ['perc_dias_acima_benchmark', 'Dias acima do benchmark', 'percent'],
  ['perc_meses_acima_benchmark', 'Meses acima do benchmark', 'percent'],
  ['sharpe', 'Sharpe', 'number'],
  ['tracking_error', 'Tracking error', 'percent'],
  ['volatilidade', 'Volatilidade', 'percent'],
  ['pl', 'PL', 'currency'],
  ['cotistas', 'Cotistas', 'integer'],
  ['gestor_pl', 'PL Gestor', 'currency'],
  ['gestor_cotistas', 'Cotistas Gestor', 'integer'],
  ['adm_pl', 'PL Adm.', 'currency'],
  ['adm_cotistas', 'Cotistas Adm.', 'integer']
];

/** Configuração hardcoded dos Chips exibidos nos resultados. */
const wResultChipConfig = {
  visibleFilters: {
    ativo_tipo_id: true, // false ou ausente oculta o Chip
    benchmark_indicador_id: true,
    exclusivo: true,
    fechado: true,
    longo: true,
    cotas: true,
    taxa_adm: true,
    condominio_fechado: true,
    responsabilidade_limitada: true,
    qualificado: true,
    profissional: true,
    publico_alvo: true,
    distribuicao: true,
    aplicacao_min: true,
    resgate_carencia: true,
    previdenciario: true,
    estruturado: true,
    esg: true,
    retorno: true,
    perc_dias_acima_benchmark: true,
    perc_meses_acima_benchmark: true,
    sharpe: true,
    tracking_error: true,
    volatilidade: true,
    pl: true,
    cotistas: true,
    gestor_pl: true,
    gestor_cotistas: true,
    adm_pl: true,
    adm_cotistas: true,
    situacao: true
  },
  labelOverrides: {
    // taxa_adm: 'Taxa adm.'// sobrescreve
    // fechado: false // exibe somente o valor
    benchmark_indicador_id: false,
    situacao: false,
    ativo_tipo_id: false,
    fechado: 'Fundo',
    taxa_adm: 'Taxa adm.',
    responsabilidade_limitada: 'Resp. limitada',
    publico_alvo: 'Público',
    aplicacao_min: 'Apl. Mín.',
    resgate_carencia: 'Prazo para Resgate'
  }
};

/** Aplica a apresentação específica do campo informado. */
function pvFormatValue(pType, pValue, pItem, pDecimal) {
  switch (pType) {
    case 'ativoTipo':
      return pItem?.ativoTipo || pvFormatOptions(ATIVO_TIPO, pValue, 'id', 'sigla');
    case 'benchmark':
      return pvFormatOptions(INDICADORES, pValue, 'indicador_id', 'indicador_sigla');
    case 'situacao':
      return pvFormatOptions(FUNDO_SITUACAO, pValue, 'value');
    case 'boolean':
      return pvFormatBoolean(pValue);
    case 'openClosed':
      return pvFormatBoolean(pValue, ['Fechado', 'Aberto']);
    case 'publicoAlvo':
      return pvFormatOptions(PUBLICO_ALVO, pValue, 'id');
    case 'distribuicao':
      return pvFormatOptions(FUNDO_DISTRIBUICAO, pValue, 'id');
    case 'currency':
      return pvFormatNumber(pValue, 2, true);
    case 'days':
      return `${pvFormatNumber(pValue, 0)} dias`;
    case 'integer':
      return pvFormatNumber(pValue, pDecimal || 0);
    case 'percent':
      return pvFormatPercent(pValue);
    default:
      return pvFormatNumber(pValue);
  }
}

/** Resolve o rótulo configurado; falso mantém somente o valor do Chip. */
function pvResolveLabel(pName, pDefaultLabel, pConfig) {
  const xLabelOverride = pConfig.labelOverrides?.[pName];

  if (xLabelOverride === false) {
    return '';
  }

  if (typeof xLabelOverride === 'string' && xLabelOverride.trim()) {
    return xLabelOverride.trim();
  }

  return pDefaultLabel;
}

/**
 * Monta os Chips de um resultado somente para filtros aplicados e dados reais disponíveis.
 *
 * @param {Object} pFilters Estado atual dos filtros do diálogo.
 * @param {Object} pItem Resultado normalizado da pesquisa de fundos.
 * @param {string} pSort Expressão de ordenação aplicada à pesquisa.
 * @param {Object} pConfig Configuração hardcoded dos Chips.
 * @returns {{ key: string, label: string }[]} Chips prontos para apresentação.
 */
export function buildComparadorFundosResultChips(
  pFilters = {},
  pItem = {},
  pSort = '',
  pConfig = wResultChipConfig
) {
  const xSortFields = pvGetSortFields(pSort);

  return wResultChipFields.reduce((pChips, pField) => {
    const [xName, xLabel, xType, xNormalizedName, xDecimal] = pField;

    if (
      pConfig.visibleFilters?.[xName] !== true ||
      (xName === 'situacao' && pvHasOnlyImplicitSituacao(pFilters[xName])) ||
      (!pvHasValue(pFilters[xName]) && !xSortFields.includes(xName))
    ) {
      return pChips;
    }

    const xValue = pvReadValue(pItem, xName, xNormalizedName);

    if (!pvHasValue(xValue)) {
      return pChips;
    }

    const xConfiguredLabel = pvResolveLabel(xName, xLabel, pConfig);
    const xFormattedValue = pvFormatValue(xType, xValue, pItem, xDecimal);

    return [
      ...pChips,
      {
        key: xName,
        label: xConfiguredLabel ? `${xConfiguredLabel}: ${xFormattedValue}` : xFormattedValue
      }
    ];
  }, []);
}
